import { useCallback, useMemo, useState } from "react";
import { useTemplates } from "../../../useTemplates";

/** Supported notification channel values for the Type column filter. */
const NOTIFICATION_TYPE_FILTER_OPTIONS = Object.freeze([
    "APP",
    "PUSH",
    "SMS",
    "EMAIL",
    "PHYSICAL",
    "CORRESPONDENCE",
]);

/** Supported member eligibility values for the Eligibility Type column filter. */
const NOTIFICATION_ELIGIBILITY_TYPE_FILTER_OPTIONS = Object.freeze([
    "Self",
    "Spouse",
    "Child",
]);

const LOADED_ROW_OPTION_KEYS = Object.freeze({
    client: "client",
    plan: "plan",
    recipientid: "recipientId",
});

function toOption(value) {
    const stringValue = String(value ?? "").trim();
    return {
        label: stringValue,
        value: stringValue,
    };
}

function toSortedOptions(values) {
    return [...new Set(values.map((value) => String(value ?? "").trim()))]
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b))
        .map(toOption);
}

function optionMatchesSearch(option, inputValue) {
    const search = String(inputValue ?? "").trim().toLowerCase();
    if (search === "") return true;

    return String(option.label ?? "").trim().toLowerCase().includes(search);
}

const SELECT_MENU_PROPS = {
    isClearable: true,
    isSearchable: true,
    menuPlacement: "auto",
    menuPosition: "fixed",
    menuPortalTarget: typeof document === "undefined" ? undefined : document.body,
    filterOption: optionMatchesSearch,
    noOptionsMessage: () => "No matching options",
    styles: {
        menuPortal: (base) => ({ ...base, zIndex: 1000 }),
    },
};

/**
 * CreatableSelect option lists, typed input state, and commit handlers for notification column filters.
 * Dropdown options use partial match; committed filter values are full strings sent to the API.
 *
 * @param {object} params
 * @param {Record<string, unknown>[]} params.notifications Loaded queue rows for client/plan/member-id suggestions.
 * @param {Record<string, string>} params.activeFilters Applied column filters from {@link useColumnFilters}.
 * @param {(key: string, value: string | null | undefined) => void} params.setFilterValue Updates applied filters.
 */
export function useFilterSelectInput({
    notifications,
    activeFilters,
    setFilterValue,
}) {
    const { templateList } = useTemplates();
    const [inputValues, setInputValues] = useState({});

    const optionGroupsByKey = useMemo(() => {
        const loadedRowOptions = Object.fromEntries(
            Object.entries(LOADED_ROW_OPTION_KEYS).map(([filterKey, rowKey]) => [
                filterKey,
                toSortedOptions(notifications.map((row) => row?.[rowKey])),
            ]),
        );

        return {
            ...loadedRowOptions,
            template: toSortedOptions(
                templateList.map((row) => row?.template),
            ),
            type: toSortedOptions(NOTIFICATION_TYPE_FILTER_OPTIONS),
            eligibilitytype: toSortedOptions(
                NOTIFICATION_ELIGIBILITY_TYPE_FILTER_OPTIONS,
            ),
        };
    }, [notifications, templateList]);

    const clearInputValue = useCallback((key) => {
        setInputValues((prev) => ({
            ...prev,
            [key]: "",
        }));
    }, []);

    const handleInputChange = useCallback((key, value, meta) => {
        if (meta.action !== "input-change") {
            return;
        }

        setInputValues((prev) => ({
            ...prev,
            [key]: value,
        }));
    }, []);

    const handleSelectChange = useCallback(
        (key, option) => {
            setFilterValue(key, option?.value);
            clearInputValue(key);
        },
        [clearInputValue, setFilterValue],
    );

    // Fires when the user picks the CreatableSelect "create" row (label from formatCreateLabel)
    // after typing a value that is not in options; commits that string as the column filter.
    //
    // If the user enters a value that is not in the preloaded menu list, a dropdown menu with
    // just 1 item saying "Search for <value>" will display. This callback handles "creating"
    // this "Search for" menu list item so the user can also click on it to search the custom
    // search query in addition to pressing the Enter key.
    const handleCreateOption = useCallback(
        (key, value) => {
            setFilterValue(key, value);
            clearInputValue(key);
        },
        [clearInputValue, setFilterValue],
    );

    const handleSelectKeyDown = useCallback(
        (key, event) => {
            if (event.key !== "Enter") {
                return;
            }

            const value = String(inputValues[key] ?? "").trim();
            if (value === "") {
                return;
            }

            event.preventDefault();
            setFilterValue(key, value);
            clearInputValue(key);
        },
        [clearInputValue, inputValues, setFilterValue],
    );

    const getSelectValue = useCallback(
        (key) =>
            activeFilters[key] ? toOption(activeFilters[key]) : null,
        [activeFilters],
    );

    const getInputValue = useCallback(
        (key) => inputValues[key] ?? "",
        [inputValues],
    );

    const formatCreateLabel = useCallback(
        (inputValue) => `Search for "${inputValue}"`,
        [],
    );

    return {
        optionGroupsByKey,
        selectMenuProps: SELECT_MENU_PROPS,
        getSelectValue,
        getInputValue,
        formatCreateLabel,
        handleInputChange,
        handleSelectChange,
        handleCreateOption,
        handleSelectKeyDown,
    };
}
